import React, { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';

export interface ScrollableSelectOption {
  value: string;
  label: string;
}

interface ScrollableSelectProps {
  options: ScrollableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Max height of the dropdown list (scroll when more options). Default 10rem (~5 items). */
  maxListHeight?: string;
}

export const ScrollableSelect: React.FC<ScrollableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className,
  maxListHeight = '10rem',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption ? selectedOption.label : placeholder;

  return (
    <div ref={containerRef} className={clsx('relative w-full', className)}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={clsx(
          'w-full px-4 py-2 border rounded-lg text-left flex items-center justify-between gap-2',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
          'dark:bg-gray-800 dark:border-gray-700 dark:text-white',
          'border-gray-300 dark:border-gray-600',
          'bg-white dark:bg-gray-900'
        )}
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown className={clsx('h-4 w-4 shrink-0 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <ul
          className="absolute z-50 mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg py-1 overflow-y-auto"
          style={{ maxHeight: maxListHeight }}
        >
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={clsx(
                  'w-full px-4 py-2 text-left text-sm transition-colors',
                  option.value === value
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                )}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
